// Browser-notification plumbing: the OS toast, and the permission dance around
// it. The React state lives in context/NotificationContext.jsx; this file is
// the part that talks to the browser and has no React in it, so it can be
// reasoned about (and unit-tested) without a renderer.

/**
 * Can this browser show OS-level notifications at all?
 *
 * `false` on http:// origins other than localhost — the API is
 * secure-context-only — and in browsers that predate it. Production runs on
 * https://management.conceptra.co.in so this is really about a developer on a
 * LAN IP, who should see the in-app bell work perfectly and simply get no toast.
 */
export const canNotify = () => typeof window !== 'undefined' && 'Notification' in window;

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export const permissionState = () => (canNotify() ? Notification.permission : 'unsupported');

/**
 * Ask for permission.
 *
 * ⚠️ MUST BE CALLED FROM A USER GESTURE, AND ONLY ONCE THAT GESTURE MEANS
 * SOMETHING. Chrome shows this prompt a single time per origin; if the user
 * dismisses or blocks it, the only way back is the padlock menu in the address
 * bar, which nobody finds. So this is deliberately NOT called on mount or on
 * login — it is wired to an explicit "Enable desktop notifications" control, so
 * the user is answering a question they just asked themselves.
 */
export async function requestPermission() {
  if (!canNotify()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Show one OS toast for a notification.
 *
 * ⚠️ ONLY WHEN THE USER IS NOT ACTUALLY LOOKING AT THIS TAB. A toast that fires
 * while they are watching the very list it duplicates is pure noise, and on
 * Windows it steals focus from what they were typing.
 *
 * `document.hidden` alone was too strict and is why this appeared not to work
 * at all: it is FALSE for a Chrome window sitting visible behind Excel, which is
 * exactly where a CA firm's staff spend the day and exactly when a toast is
 * most wanted. Adding `document.hasFocus()` covers that case — the page is on
 * screen but is not what the user is typing into.
 *
 * ⚠️ `pushSafe` DECIDES WHETHER THE REAL TEXT LEAVES THE SESSION. An OS toast
 * renders on a lock screen and in the Windows Action Center history, outside
 * anything this app controls. Chat bodies and client names arrive with
 * pushSafe:false from the server and degrade to a generic line here rather than
 * being suppressed entirely — the person still learns something arrived, which
 * is the whole job, without the shared-office desk becoming a disclosure.
 *
 * @param {object}   n        the shaped notification from the server
 * @param {function} onOpen   called with `n` when the toast is clicked
 */
export function showOsToast(n, onOpen) {
  if (!canNotify() || Notification.permission !== 'granted') return null;
  if (typeof document !== 'undefined'
    && !document.hidden
    && (typeof document.hasFocus !== 'function' || document.hasFocus())) return null;

  const title = n.pushSafe ? n.title : 'New notification';
  const body = n.pushSafe ? n.body || '' : 'Open the app to see it.';

  try {
    const toast = new Notification(title, {
      body,
      // The notification id, so a re-delivery on reconnect replaces the toast
      // in place instead of stacking a second identical one.
      tag: n.id,
      // Same file the service worker uses. /logo192.png was CRA's default name
      // and never existed here, so every toast from this path fell back to a
      // generic browser icon.
      icon: '/icon-192.png',
      // No `requireInteraction`: these should age out of the Action Center on
      // their own. A notification that must be dismissed by hand is one the
      // user will disable at the OS level within a day.
    });
    toast.onclick = () => {
      // The tab is hidden by definition here, so it has to be raised before the
      // route change or the navigation happens somewhere nobody is looking.
      window.focus();
      toast.close();
      onOpen?.(n);
    };
    return toast;
  } catch {
    // Some browsers throw on construction (notably older Android Chrome, which
    // requires a service worker). Not worth branching on — no toast, bell only.
    return null;
  }
}

/**
 * Mirror the unread total onto the taskbar/dock icon.
 *
 * No-ops unless the app has been installed as a PWA, which is why it is called
 * unconditionally and never checked: three lines that quietly do nothing for
 * most users and something nice for the ones who pinned the app.
 */
// ── Web Push ────────────────────────────────────────────────────────────────
//
// The layer that reaches a browser with no tab open. Everything above works
// only while a page is running; this hands the browser vendor a subscription
// and lets the service worker be woken instead.

/** Does this browser support the pieces push needs? */
export const canPush = () => (
  typeof navigator !== 'undefined'
  && 'serviceWorker' in navigator
  && typeof window !== 'undefined'
  && 'PushManager' in window
);

// The VAPID public key arrives as base64url text and `subscribe()` wants raw
// bytes. Padding and the two substituted characters have to be undone first —
// getting this wrong fails deep inside the browser with an opaque error.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * Register the service worker. Safe to call repeatedly — the browser returns
 * the existing registration rather than installing a second one.
 */
export async function registerServiceWorker() {
  if (!canPush()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch {
    return null;
  }
}

/**
 * Subscribe this browser to push, returning the subscription for the server.
 *
 * ⚠️ REUSES AN EXISTING SUBSCRIPTION WHEN THERE IS ONE. Calling `subscribe()`
 * again with the same key returns the same endpoint, but calling it with a
 * DIFFERENT key throws — so a rotated VAPID pair needs the old subscription
 * dropped first, which is what the mismatch branch does.
 */
export async function subscribeToPush(vapidPublicKey) {
  if (!canPush() || !vapidPublicKey) return null;
  if (Notification.permission !== 'granted') return null;

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();

  if (existing) {
    const current = existing.options?.applicationServerKey;
    const wanted = urlBase64ToUint8Array(vapidPublicKey);
    const same = current
      && new Uint8Array(current).length === wanted.length
      && new Uint8Array(current).every((b, i) => b === wanted[i]);
    if (same) return existing.toJSON();
    // Key rotated on the server — the old endpoint can never be signed for
    // again, so drop it before asking for a new one.
    await existing.unsubscribe().catch(() => {});
  }

  const sub = await reg.pushManager.subscribe({
    // Required by Chrome: a push whose payload the user cannot be shown is not
    // allowed, and passing false here is rejected outright.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  });
  return sub.toJSON();
}

/** The current subscription's endpoint, so the server can drop the right row. */
export async function currentPushEndpoint() {
  if (!canPush()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? sub.endpoint : null;
  } catch {
    return null;
  }
}

export async function unsubscribeFromPush() {
  if (!canPush()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return null;
    const { endpoint } = sub;
    await sub.unsubscribe();
    return endpoint;
  } catch {
    return null;
  }
}

export function setAppBadge(total) {
  if (typeof navigator === 'undefined') return;
  try {
    if (total > 0) navigator.setAppBadge?.(total);
    else navigator.clearAppBadge?.();
  } catch {
    /* Badging API unavailable or blocked — nothing to do. */
  }
}
